/**
 * Icon registry for MapLibre map markers.
 *
 * Reads VITE_MAP_ICON_PACK env variable:
 *   "argos"  → loads from src/assets/map-argos/ (Iconify-generated icons)
 *   "kalan"  → loads from src/assets/map/ (original hand-drawn SVGs)
 *   default  → "kalan"
 *
 * VITE_USE_GENERATED_ICONS='true' activa el sistema declarativo nuevo
 * (AJUSTE-PALETA-MARKERS): lee desde theming/generated/<tema-activo>/
 * SVGs pre-pintados según paleta del tema, sin recolor en runtime.
 * Fallback a los packs legacy (argos/kalan) si falta el SVG pre-generado.
 *
 * Only affects map-v2. The original /map route is unchanged.
 */

const ICON_SIZE = 40
const ICON_PACK = import.meta.env.VITE_MAP_ICON_PACK || 'kalan'
const USE_GENERATED = String(import.meta.env.VITE_USE_GENERATED_ICONS || '').toLowerCase() === 'true'

// SVGs del sistema declarativo de temas, cargados LAZY por chunk de tema.
// `import.meta.glob` sin `eager` devuelve { [path]: () => Promise<string> }:
// las KEYS son conocidas al module-load (Vite las inyecta), pero el CONTENIDO
// del SVG solo se descarga cuando se invoca el loader.
//
// Antes este glob era `eager: true` y traia los 4 temas completos al bundle
// principal (~2024 SVGs), de los cuales se usaban solo 506 (tema activo).
// Con lazy, Vite chunkea cada tema en su propio archivo lazy — el shell solo
// descarga el tema activo. Cambiar de tema dispara la descarga del nuevo.
//
// Los packs legacy (svgKalan, svgArgos) que solo se usaban con USE_GENERATED=
// false fueron eliminados — el sistema de temas declarativo es definitivo y
// el flag VITE_USE_GENERATED_ICONS=true en .env de producción.
const svgGeneratedLazy = import.meta.glob('/src/components/map-v2/theming/generated/**/*.svg', { as: 'raw' })

// Temas CSS sin paquete de SVGs de markers propio reusan el de otro tema.
// 'diurno' (tema claro) reusa los markers de 'vibrante' (su versión diurna,
// los más saturados → mejor contraste sobre mapa claro) hasta que se genere
// una paleta de markers clara dedicada. DEBE quedar sincronizado con el
// alias de apply-markers.js para que los SVGs del mapa y los `--kln-marker-*`
// de los toggles/chips vengan del mismo tema.
const MARKER_THEME_ALIAS = { diurno: 'vibrante' }

function getActiveTheme() {
  let theme = 'desaturado'
  try {
    const persisted = window && window.localStorage && window.localStorage.getItem('kalan:theme')
    if (persisted) theme = persisted
  } catch (_) { /* ignore */ }
  return MARKER_THEME_ALIAS[theme] || theme
}

// Glob LAZY (url) solo de los SVGs de device generados. Permite que componentes
// Vue (p.ej. el avatar del drawer) muestren EXACTAMENTE el mismo asset que el
// marker del mapa (glyph + color del container + tema), sin pasar por el
// pipeline de bitmaps de MapLibre ni bundlear todos los SVGs eager.
const svgDeviceUrlLazy = import.meta.glob(
  '/src/components/map-v2/theming/generated/**/device_*.svg',
  { as: 'url' }
)

// Resuelve (async) la URL del SVG del marker de un device para el tema activo.
// slug: del device-type-resolver; status: online|offline|unknown|down;
// mapMode: 'day'|'night'. Devuelve null si no existe la variante.
export function loadDeviceMarkerSvgUrl(slug, status, mapMode) {
  try {
    const theme = getActiveTheme()
    const mode = mapMode === 'night' ? 'night' : 'day'
    const st = ['online', 'offline', 'unknown', 'down'].indexOf(status) >= 0 ? status : 'offline'
    const key = '/src/components/map-v2/theming/generated/' + theme + '/device_' + slug + '_' + mode + '_' + st + '.svg'
    const loader = svgDeviceUrlLazy[key]
    if (!loader) return Promise.resolve(null)
    return loader().then(function(m) { return (m && m.default) || m || null }).catch(function() { return null })
  } catch (e) {
    return Promise.resolve(null)
  }
}

// Devuelve { [iconKey]: () => Promise<string> } solo del tema activo.
// Las keys del glob lazy son conocidas al module-load, asi que filtrar por
// prefix no requiere cargar contenido alguno.
function pickActiveThemeLoaders() {
  if (!USE_GENERATED) {
    throw new Error('[icon-registry] VITE_USE_GENERATED_ICONS debe estar activo en .env')
  }
  const activeTheme = getActiveTheme()
  const prefix = '/src/components/map-v2/theming/generated/' + activeTheme + '/'
  const out = {}
  for (const key of Object.keys(svgGeneratedLazy)) {
    if (key.startsWith(prefix)) {
      const flat = key.slice(prefix.length)
      out['/' + activeTheme + '/' + flat] = svgGeneratedLazy[key]
    }
  }
  if (Object.keys(out).length === 0) {
    throw new Error('[icon-registry] No hay SVGs generados para tema "' + activeTheme + '". Corre `npm run theme:generate`.')
  }
  return out
}

const svgLoaders = pickActiveThemeLoaders()

export const AVAILABLE_ICONS = new Set(
  Object.keys(svgLoaders).map(function(p) { return p.split('/').pop().replace('.svg', '') })
)

export function hasIcon(name) {
  return AVAILABLE_ICONS.has(name)
}

// Mapeo de prefijo de icono argos → token de color semántico del tema.
// Cada entrada se resuelve al hex del tema activo via CSS vars `--kln-<token>`.
// Las variantes de sufijo (_day_up, _night_down, etc.) se remueven antes de
// buscar en este mapa — ver `_getIconBaseName`.
// Reglas: 1 color = 1 rol operativo. El icono interior distingue sub-tipos.
// entity-violet queda reservado — no se asigna a ningún rol operativo hoy.
// 7 colores en total:
//   blue      = policía (todas las corporaciones)
//   teal      = vigilancia electrónica fija (camera, LPR, external sensor)
//   gold      = GN + militar
//   green     = transporte público + salud fija (bus, taxi, CPR)
//   orange    = respuesta emergencia física (fire, bombero, PC, ambulancia)
//   red       = alertas activas 911 (incident, sensor pánico)
//   alert-sos = pánico ciudadano (canal dedicado, saturado)
//   idle      = info/seguimiento pasivo (sismo, presa, reportant, route_lpr)
const ARGOS_ICON_COLOR_TOKENS = {
  ambulancia: 'entity-orange',
  bombero: 'entity-orange',
  boton_panico: 'status-alert',   // ámbar "armado"; el halo rojo aparece solo en alert
  bus: 'entity-green',
  camera: 'entity-teal',
  cpr: 'entity-green',
  default: 'entity-blue',
  elemento_pc: 'entity-orange',
  elemento_pc_cam: 'entity-orange',
  external_sensor: 'entity-teal',
  external_sensor_route: 'entity-teal',
  fire: 'entity-orange',
  // Incidentes 9-1-1: cada categoría del CNIE v3.0 (SNSP) con su color.
  // El glow rojo alrededor del marker señala "es un incidente"; el color
  // del fondo del icono distingue la categoría.
  //
  // Seguridad conserva el color default del producto (entity-red, el
  // rojo-naranja del Sistema B) — es la categoría mayoritaria y el
  // operador ya la asocia con ese tono. Los demás grupos heredan los
  // tokens de KPI cuando aplica, y mal_uso se pinta con entity-violet
  // (reservado del Sistema B) para no solaparse con seguridad.
  // Apunta directo a los tokens cat-<slug> del manual federal — fuente
  // única de verdad de la paleta de incidentes 911 (theming/palettes).
  // Solo se usa en path legacy con USE_GENERATED=false; con SVGs
  // pre-pintados activos los iconos vienen ya con el color del tema.
  incident_asistencia:         'cat-asistencia',
  incident_mal_uso:            'cat-mal-uso',
  incident_medico:             'cat-medico',
  incident_movement:           'status-idle',
  incident_movement_end:       'status-idle',
  incident_otros_servicios:    'cat-otros-servicios',
  incident_proteccion_civil:   'cat-proteccion-civil',
  incident_salud:              'cat-medico',      // alias de médicos
  incident_seguridad:          'cat-seguridad',
  incident_servicios_publicos: 'cat-servicios-publicos',
  lpr: 'entity-teal',
  militar: 'entity-gold',
  patrulla_estatal: 'entity-blue',
  patrulla_estatal_cam: 'entity-blue',
  patrulla_gnacional: 'entity-gold',
  patrulla_gnacional_cam: 'entity-gold',
  patrulla_ministerial: 'entity-blue',
  patrulla_ministerial_cam: 'entity-blue',
  patrulla_municipal: 'entity-blue',
  patrulla_municipal_cam: 'entity-blue',
  policia_estatal: 'entity-blue',
  policia_estatal_cam: 'entity-blue',
  policia_ministerial: 'entity-blue',
  policia_ministerial_cam: 'entity-blue',
  policia_municipal: 'entity-blue',
  policia_municipal_cam: 'entity-blue',
  presa: 'status-idle',
  proteccion_civil: 'entity-orange',
  reportant: 'status-idle',
  route_lpr: 'status-idle',
  sensor: 'status-alert',         // ámbar "armado"; el halo rojo aparece solo en alert
  sismo: 'status-idle',
  sos: 'alert-sos',
  taxi: 'entity-green',
}

// Sufijos conocidos del naming convention: {icon}_{mapMode}_{iconStatus}.
// Los removemos antes de buscar en ARGOS_ICON_COLOR_TOKENS.
function _getIconBaseName(name) {
  return name.replace(/_(day|night)_(up|down)$/, '').replace(/_(up|down)$/, '')
}

function _readThemeHex(token) {
  const val = getComputedStyle(document.documentElement)
    .getPropertyValue('--kln-' + token).trim()
  return val || null
}

// Reemplaza SOLO el primer `fill="#XXXXXX"` del SVG — que en los iconos
// argos es el rect de fondo. Los paths internos `fill="#FFFFFF"` quedan
// intactos (siguen blancos sobre el nuevo bg).
function _recolorArgosSvg(svgContent, hexColor) {
  return svgContent.replace(/fill="#[0-9A-Fa-f]{3,6}"/, 'fill="' + hexColor + '"')
}

// Legacy: recolor del SVG argos en runtime. Se mantiene como fallback
// cuando VITE_USE_GENERATED_ICONS está en false. Cuando el sistema
// declarativo de temas está activo, los SVGs ya vienen pintados desde
// theming/generated/<tema>/ — no se llama esta función.
// Variantes `_down` preservan su fill original (gris de "apagado").
export function maybeRecolorSvg(name, svgContent) {
  if (USE_GENERATED) return svgContent  // los generados ya vienen pintados
  if (ICON_PACK !== 'argos') return svgContent
  if (/_down$/.test(name)) return svgContent
  const baseName = _getIconBaseName(name)
  const token = ARGOS_ICON_COLOR_TOKENS[baseName]
  if (!token) return svgContent
  const hex = _readThemeHex(token)
  if (!hex) return svgContent
  return _recolorArgosSvg(svgContent, hex)
}

// Concurrencia del decode de SVGs. svgToImage usa Image.onload con un
// blob URL — el cuello es el decoder del browser, no el main thread. Las
// imagenes se decodifican en paralelo en threads del worker pool. 16 es
// suficiente para saturar el pool sin matar el FD limit del browser.
const ICON_REGISTER_CONCURRENCY = 16

// IndexedDB cache para bitmaps decodificados. La primera carga paga el
// decode completo (~2.3s con 506 SVGs); las recargas siguientes leen
// los ImageBitmap directos del IDB (~50ms). Invalidamos cuando los SVGs
// cambian via hash del contenido del bundle.
const ICON_CACHE_DB = 'kalan-icon-cache'
const ICON_CACHE_STORE = 'bitmaps'
const ICON_CACHE_DB_VERSION = 1

let _iconDbPromise = null
function openIconDb() {
  if (_iconDbPromise) return _iconDbPromise
  _iconDbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('no idb')); return }
    const req = indexedDB.open(ICON_CACHE_DB, ICON_CACHE_DB_VERSION)
    req.onupgradeneeded = (e) => {
      const db = e.target.result
      if (!db.objectStoreNames.contains(ICON_CACHE_STORE)) {
        db.createObjectStore(ICON_CACHE_STORE)
      }
    }
    req.onsuccess = (e) => resolve(e.target.result)
    req.onerror = () => reject(req.error)
  }).catch((e) => { _iconDbPromise = null; throw e })
  return _iconDbPromise
}

// Versión del bundle SVG. Bumpear este número cuando:
//   - Se cambian los paths MDI internos de algún SVG fuente
//   - Se cambian los hex de paletas (theming/palettes/*.js + theme:generate)
//   - Cualquier cambio que afecte el contenido pero NO los nombres de archivo
// Sin esto, el cache IDB se queda con los bitmaps viejos al recargar
// (el hash se basa en nombres, no en contenido) y el operador no ve los
// cambios de color/icono hasta hacer hard reload + delete IndexedDB.
const ICON_BUNDLE_VERSION = 4

// Hash estable del bundle SVG. Con el glob lazy las KEYS son conocidas pero
// el contenido no esta cargado, asi que el hash se basa en (themeKey + sorted
// keys + count + ICON_BUNDLE_VERSION). Bumpear ICON_BUNDLE_VERSION manual
// cuando solo cambia el contenido sin renombrar archivos.
function computeBundleHash() {
  const keys = Object.keys(svgLoaders).sort()
  const themeKey = getActiveTheme()
  let h = 5381
  for (let i = 0; i < themeKey.length; i++) {
    h = (((h << 5) + h) + themeKey.charCodeAt(i)) | 0
  }
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i]
    for (let j = 0; j < k.length; j++) {
      h = (((h << 5) + h) + k.charCodeAt(j)) | 0
    }
  }
  h = (((h << 5) + h) + keys.length) | 0
  h = (((h << 5) + h) + ICON_BUNDLE_VERSION) | 0
  return String(h >>> 0)
}

// Helpers genericos de cache de bitmaps en IDB. El namespace separa
// los bundles (registerAllIcons usa 'icons:<theme>' por convencion;
// otros consumers como warmClusterIconCacheAsync pueden reusar la
// misma DB con su propia key).
export async function getBitmapCacheEntry(key, expectedHash) {
  return getCachedIconBundle(key, expectedHash)
}

export async function setBitmapCacheEntry(key, hash, bitmaps) {
  return setCachedIconBundle(key, hash, bitmaps)
}

async function getCachedIconBundle(themeKey, expectedHash) {
  try {
    const db = await openIconDb()
    return await new Promise((resolve) => {
      const tx = db.transaction(ICON_CACHE_STORE, 'readonly')
      const store = tx.objectStore(ICON_CACHE_STORE)
      const req = store.get(themeKey)
      req.onsuccess = () => {
        const entry = req.result
        if (!entry) { resolve(null); return }
        if (entry.hash !== expectedHash) { resolve(null); return }  // stale
        resolve(entry)
      }
      req.onerror = () => resolve(null)
    })
  } catch (_) { return null }
}

async function setCachedIconBundle(themeKey, hash, bitmaps) {
  try {
    const db = await openIconDb()
    await new Promise((resolve) => {
      const tx = db.transaction(ICON_CACHE_STORE, 'readwrite')
      tx.objectStore(ICON_CACHE_STORE).put({
        hash,
        bitmaps,
        createdAt: Date.now(),
      }, themeKey)
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
    })
  } catch (_) {}
}

export async function registerAllIcons(map) {
  try { performance.mark('boot.icons-register.start') } catch (_) {}

  const themeKey = getActiveTheme()
  const bundleHash = computeBundleHash()

  // FAST PATH: cache hit en IDB. Recargas con el mismo bundle leen
  // ImageBitmap pre-decodificados (structured-clone de IDB es nativo).
  // ~50ms total vs ~2.3s del path lento de decode.
  const cacheReadStart = performance.now()
  const cached = await getCachedIconBundle(themeKey, bundleHash)
  const cacheReadEnd = performance.now()
  let cacheHit = 0
  if (cached && cached.bitmaps && typeof cached.bitmaps === 'object') {
    const addImageStart = performance.now()
    const names = Object.keys(cached.bitmaps)
    for (let i = 0; i < names.length; i++) {
      const name = names[i]
      // En cache hit los bitmaps son identicos al que ya esta registrado
      // (mismo hash). Skipear el remove+add ahorra postMessages al worker
      // MapLibre. addImage solo si no existe — idempotente.
      if (!map.hasImage(name)) {
        try {
          map.addImage(name, cached.bitmaps[name], { sdf: false })
          cacheHit++
        } catch (err) {
          console.warn('[icon-registry] cache addImage failed:', name, err)
        }
      } else {
        cacheHit++
      }
    }
    const addImageEnd = performance.now()
    try {
      if (window.__bootProfile) {
        window.__bootProfile.custom = window.__bootProfile.custom || {}
        window.__bootProfile.custom.iconsCacheReadMs = Math.round(cacheReadEnd - cacheReadStart)
        window.__bootProfile.custom.iconsCacheAddImageMs = Math.round(addImageEnd - addImageStart)
      }
    } catch (_) {}
    await registerPartnerBadges(map)
    try {
      performance.mark('boot.icons-register.end')
      performance.measure('boot.icons-register', 'boot.icons-register.start', 'boot.icons-register.end')
      if (window.__bootProfile) {
        window.__bootProfile.custom = window.__bootProfile.custom || {}
        window.__bootProfile.custom.iconsRegistered = cacheHit
        window.__bootProfile.custom.iconsCacheHit = true
        window.__bootProfile.custom.iconsCacheBundleHash = bundleHash
      }
    } catch (_) {}
    return
  }

  // SLOW PATH: cache miss. Decode todos los SVGs en paralelo (pool de
  // 16 workers async — saturan el decoder pool del browser). Acumulamos
  // los ImageBitmap en `decodedBitmaps` para guardarlos al final en IDB.
  //
  // Con el glob lazy, cada `loader()` dispara la descarga del chunk del SVG
  // (o lo lee del modulo cache si el chunk ya esta en memoria). En la primera
  // carga el costo es la red; las recargas con HTTP cache leen del disco.
  const entries = Object.entries(svgLoaders)
  const decodedBitmaps = {}  // { [name]: ImageBitmap }
  let n = 0
  let cursor = 0
  const worker = async () => {
    while (cursor < entries.length) {
      const idx = cursor++
      const [path, loader] = entries[idx]
      const name = path.split('/').pop().replace('.svg', '')
      if (map.hasImage(name)) {
        try { map.removeImage(name) } catch (_) { /* ignore */ }
      }
      try {
        const svgContent = await loader()
        const colored = maybeRecolorSvg(name, svgContent)
        const tDecodeStart = performance.now()
        const img = await svgToImage(colored, ICON_SIZE, ICON_SIZE)
        const tDecodeEnd = performance.now()
        // Convertimos a ImageBitmap antes de pasar a map.addImage Y
        // antes de cachear: ImageBitmap es structured-cloneable (IDB
        // lo serializa nativamente) y MapLibre tambien lo acepta.
        // Image (HTMLImageElement) no es serializable por structured-clone.
        let bitmap = img
        if (typeof createImageBitmap === 'function' && typeof ImageBitmap !== 'undefined' && !(img instanceof ImageBitmap)) {
          try { bitmap = await createImageBitmap(img) } catch (_) { /* keep img */ }
        }
        if (!map.hasImage(name)) {
          map.addImage(name, bitmap, { sdf: false })
        }
        if (bitmap instanceof (typeof ImageBitmap !== 'undefined' ? ImageBitmap : Object)) {
          decodedBitmaps[name] = bitmap
        }
        const tAddEnd = performance.now()
        try {
          if (window.__bootProfile) {
            window.__bootProfile.count('icons.decode', tDecodeEnd - tDecodeStart)
            window.__bootProfile.count('icons.addImage', tAddEnd - tDecodeEnd)
          }
        } catch (_) {}
        n++
      } catch (err) {
        console.warn('[icon-registry] Failed:', name, err)
      }
    }
  }

  const workers = []
  for (let i = 0; i < ICON_REGISTER_CONCURRENCY; i++) workers.push(worker())
  await Promise.all(workers)

  await registerPartnerBadges(map)

  // Persistir el bundle decodificado en IDB para acelerar recargas
  // futuras. Lo hacemos fire-and-forget (no bloqueamos el boot).
  if (Object.keys(decodedBitmaps).length > 0) {
    setCachedIconBundle(themeKey, bundleHash, decodedBitmaps).catch(() => {})
  }

  try {
    performance.mark('boot.icons-register.end')
    performance.measure('boot.icons-register', 'boot.icons-register.start', 'boot.icons-register.end')
    if (window.__bootProfile) {
      window.__bootProfile.custom = window.__bootProfile.custom || {}
      window.__bootProfile.custom.iconsRegistered = n
      window.__bootProfile.custom.iconsCacheHit = false
      window.__bootProfile.custom.iconsCacheBundleHash = bundleHash
    }
  } catch (_) {}
}

// Precarga los logos PNG de los proveedores de interconexión externa
// (Carbyne / DiDi / Uber / RapidSOS) como sprites del mapa, indexados
// por la convención `partner-badge-<id>`. El layer `unified-partner-
// badge` los referencia con la expresión:
//   ['concat', 'partner-badge-', ['get', 'partner']]
//
// Los PNGs viven en `public/` (no se importan vía bundler). Tamaño base
// 48×48 — suficiente resolución para que el icon-size del layer (0.55
// → ~26px efectivos) se vea nítido en pantallas HiDPI. Se mantienen
// como PNG (no se convierten a SDF ni se recolorean) porque son logos
// de marca y deben verse 1:1 igual que en el legacy.
const PARTNER_BADGE_SOURCES = {
  carbyne: '/carbyne_small.png',
  didi: '/didi.png',
  uber: '/uber.png',
  rapidsos: '/rapidsos.png',
}
const PARTNER_BADGE_SIZE = 48

async function registerPartnerBadges(map) {
  const t0 = performance.now()
  // FAST PATH: cache IDB. Los partner badges son PNGs estaticos en
  // public/ que solo cambian si se reemplazan los assets — un hash
  // basado solo en su urlset es suficiente para invalidar.
  const cacheKey = 'partners:v1'
  const cacheHash = Object.keys(PARTNER_BADGE_SOURCES).sort().join(',') + ':' + PARTNER_BADGE_SIZE
  try {
    const cached = await getCachedIconBundle(cacheKey, cacheHash)
    if (cached && cached.bitmaps) {
      const names = Object.keys(cached.bitmaps)
      for (let i = 0; i < names.length; i++) {
        const name = names[i]
        if (!map.hasImage(name)) {
          try { map.addImage(name, cached.bitmaps[name], { sdf: false }) } catch (_) {}
        }
      }
      try {
        if (window.__bootProfile) {
          window.__bootProfile.custom = window.__bootProfile.custom || {}
          window.__bootProfile.custom.partnerBadgesTotalMs = Math.round(performance.now() - t0)
          window.__bootProfile.custom.partnerBadgesCacheHit = true
        }
      } catch (_) {}
      return
    }
  } catch (_) {}

  // SLOW PATH: paralelizar las 4 descargas + composiciones. Antes era
  // for-await en serie, asi que un solo PNG lento (rapidsos = 3.5s)
  // bloqueaba a los otros 3 (que tardan ~70-120ms cada uno). Con
  // Promise.all el tiempo total es max(durations) ~ 3.5s en lugar de
  // sum(durations) ~ 3.7s. Ademas el cache evita el costo en recargas.
  const entries = Object.entries(PARTNER_BADGE_SOURCES)
  const decoded = {}  // {name: ImageBitmap}
  const tasks = entries.map(async ([id, src]) => {
    const name = 'partner-badge-' + id
    if (map.hasImage(name)) return
    try {
      const tBadgeStart = performance.now()
      const composed = await composePartnerBadge(src, PARTNER_BADGE_SIZE)
      map.addImage(name, composed, { sdf: false })
      // composePartnerBadge ya devuelve ImageBitmap (createImageBitmap
      // del canvas resultante). Es structured-cloneable → cacheable.
      if (typeof ImageBitmap !== 'undefined' && composed instanceof ImageBitmap) {
        decoded[name] = composed
      }
      try {
        if (window.__bootProfile) {
          window.__bootProfile.count('partner.' + id, performance.now() - tBadgeStart)
        }
      } catch (_) {}
    } catch (err) {
      console.warn('[icon-registry] partner badge failed:', id, err)
    }
  })
  await Promise.all(tasks)

  // Persistir el bundle de partners en IDB para recargas futuras.
  if (Object.keys(decoded).length > 0) {
    setCachedIconBundle(cacheKey, cacheHash, decoded).catch(() => {})
  }

  try {
    if (window.__bootProfile) {
      window.__bootProfile.custom = window.__bootProfile.custom || {}
      window.__bootProfile.custom.partnerBadgesTotalMs = Math.round(performance.now() - t0)
      window.__bootProfile.custom.partnerBadgesRegistered = Object.keys(decoded).length
      window.__bootProfile.custom.partnerBadgesCacheHit = false
    }
  } catch (_) {}
}

// Compone el badge final: fondo blanco con bordes redondeados +
// el logo del proveedor centrado encima. Esto garantiza legibilidad
// sobre cualquier color de marker base (los PNGs originales tienen
// fondo transparente y trazo oscuro — se perdían sobre markers
// rojos/naranjas/etc).
async function composePartnerBadge(src, size) {
  const img = await loadHtmlImage(src)
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  // Fondo blanco con border-radius. ~6px de radio sobre 48×48 da el
  // mismo "chip rounded" que tienen los logos en sidebar/drawer.
  const radius = Math.round(size * 0.18)
  ctx.fillStyle = '#FFFFFF'
  roundedRect(ctx, 0, 0, size, size, radius)
  ctx.fill()
  // Logo centrado con padding interno para que respire.
  const padding = Math.round(size * 0.12)
  const inner = size - padding * 2
  // Mantener proporciones del logo original.
  const ratio = Math.min(inner / img.width, inner / img.height)
  const w = img.width * ratio
  const h = img.height * ratio
  const x = (size - w) / 2
  const y = (size - h) / 2
  ctx.drawImage(img, x, y, w, h)
  // ImageBitmap se requiere para `map.addImage` con fidelidad pixel-
  // perfect. canvas.transferToImageBitmap es el camino moderno;
  // fallback a getImageData para browsers sin OffscreenCanvas-style.
  if (typeof createImageBitmap === 'function') {
    return await createImageBitmap(canvas)
  }
  return canvas
}

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = (err) => reject(err)
    img.src = src
  })
}

// Probamos createImageBitmap directo desde blob SVG y EMPEORO el tiempo
// (3480ms → 3807ms) probablemente por overhead del resizeQuality 'high'
// en SVGs de 40x40 (rasterizar a alta calidad un SVG simple es mas
// caro que cargarlo via Image). Volvemos a Image.onload + el metodo
// que ya teniamos. La concurrencia del pool de 16 sigue activa (es lo
// unico que dio mejora medible: -214ms).
function svgToImage(svgString, width, height) {
  return new Promise((resolve, reject) => {
    const img = new Image(width, height)
    const blob = new Blob([svgString], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = (err) => { URL.revokeObjectURL(url); reject(err) }
    img.src = url
  })
}

/**
 * Build MapLibre expression for icon-image with fallbacks.
 * Naming: {icon}_{mapMode}_{iconStatus} → {icon}_{iconStatus} → {icon}_up → {icon}
 */
export function buildIconExpression() {
  return [
    'coalesce',
    ['image', ['concat', ['get', 'icon'], '_', ['get', 'mapMode'], '_', ['get', 'iconStatus']]],
    ['image', ['concat', ['get', 'icon'], '_', ['get', 'iconStatus']]],
    ['image', ['concat', ['get', 'icon'], '_up']],
    ['image', ['get', 'icon']],
  ]
}
